import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import MetricCard from "../../performance/MetricCard";
import {
  PerformanceContainer,
  MetricsGrid,
  ChartsGrid,
} from "../../performance/PerformanceStyles";
import {
  fetchConversionMetrics,
  fetchPieChartData,
  fetchDateYearMetrics,
  resetLocationTreeState,
} from "store/performance-report/PerformanceReportActions";
import { MetricCategory } from "store/performance-report/PerformanceReportTypes";
import {
  useSetGoalHandler,
  useDeleteGoalHandler,
} from "hooks/useSetGoalHandler";
import {
  selectPerformanceReportStoreId,
  selectPerformanceReportDateRange,
  selectIsReportAPIAvailable,
  selectConversionMetrics,
  selectDateYearMetrics,
  selectPieChartDataByMetricId,
  selectIsConversionMetricsAlreadyFetched,
} from "store/performance-report/PerformanceReportSelectors";
import {
  selectKpiCategoryById,
  selectKpiConfigIsLoaded,
} from "store/kpi-config/KpiConfigSelectors";
import {
  getBasicMetricsConfig,
  getPieChartMetricsConfig,
  convertBasicMetricsToPayload,
  getAllMetricIds,
  getMetricsByDisplayType,
  getAllPieChartApiConfigs,
  getTreeChartConfig,
  getMetricGoal,
  isMetricGoalEnabled,
} from "utils/KpiConfigUtils";
import {
  calculateDateRanges,
  transformMetricsData,
  transformPieChartsData,
  getDaysFromPeriodType,
  calculateDimensionId,
  calculateGranularity,
  createMetricLineCharts,
} from "utils/PerformanceReportUtils";
import { useTheme } from "@mui/material/styles";
import PieChart from "components/performance/PieChart";
import LocationTreeView from "components/performance/LocationTreeView";
import AppState from "store/AppState";

const ConversionComponent = () => {
  const intl = useIntl();
  const theme = useTheme();
  const dispatch = useDispatch();
  const performanceStoreId = useSelector(selectPerformanceReportStoreId);
  const performanceDateRange = useSelector(selectPerformanceReportDateRange);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  const conversionMetricsData = useSelector(selectConversionMetrics);
  const isKpiConfigLoaded = useSelector(selectKpiConfigIsLoaded);
  const conversionCategory = useSelector((state: any) =>
    selectKpiCategoryById("conversion")(state),
  );
  const isAlreadyFetched = useSelector(selectIsConversionMetricsAlreadyFetched);

  const conversionMetricsConfig = useMemo(() => {
    return isKpiConfigLoaded && conversionCategory
      ? getBasicMetricsConfig(conversionCategory)
      : [];
  }, [isKpiConfigLoaded, conversionCategory]);

  const conversionPieChartsConfig = useMemo(() => {
    return isKpiConfigLoaded && conversionCategory
      ? getPieChartMetricsConfig(conversionCategory)
      : [];
  }, [isKpiConfigLoaded, conversionCategory]);

  const pieChartMetricIds = useMemo(() => {
    return conversionPieChartsConfig.map((config: any) => config.id);
  }, [conversionPieChartsConfig]);

  const pieChartsData = useSelector((state: AppState) => {
    const dataMap: Record<string, { data: any; isLoading: boolean }> = {};
    pieChartMetricIds.forEach((metricId: string) => {
      dataMap[metricId] = selectPieChartDataByMetricId(metricId)(state);
    });
    return dataMap;
  });

  const treeChartConfig = useMemo(() => {
    return isKpiConfigLoaded && conversionCategory
      ? getTreeChartConfig(conversionCategory)
      : null;
  }, [isKpiConfigLoaded, conversionCategory]);

  const conversionMetrics = transformMetricsData(
    conversionMetricsConfig,
    conversionMetricsData,
    intl,
  );

  const conversionPieCharts = transformPieChartsData(
    conversionPieChartsConfig,
    pieChartsData,
    intl,
    theme,
  );

  const dateYearMetricsData = useSelector(
    selectDateYearMetrics(MetricCategory.CONVERSION),
  );
  const dateRangeDays = getDaysFromPeriodType(performanceDateRange);
  const dimensionId = calculateDimensionId(dateRangeDays);
  const granularity = calculateGranularity(dateRangeDays);
  const metricLineCharts = createMetricLineCharts(
    dateYearMetricsData,
    conversionMetricsConfig,
    granularity,
    intl,
    "conversion",
  );

  useEffect(() => {
    if (isAlreadyFetched) {
      return;
    }

    if (
      performanceStoreId &&
      performanceDateRange &&
      isReportAPIAvailable &&
      isKpiConfigLoaded &&
      conversionCategory
    ) {
      const { currentPeriod, comparisonPeriod } =
        calculateDateRanges(performanceDateRange);
      const basicMetrics = getMetricsByDisplayType(conversionCategory, "basic");
      const metricsPayload = convertBasicMetricsToPayload(basicMetrics);

      dispatch(
        fetchConversionMetrics(
          performanceStoreId,
          currentPeriod,
          comparisonPeriod,
          metricsPayload,
        ),
      );

      const pieChartConfigs = getAllPieChartApiConfigs(conversionCategory);
      pieChartConfigs.forEach((config) => {
        dispatch(
          fetchPieChartData({
            storeId: performanceStoreId,
            metricId: config.metricId,
            dateRange: currentPeriod,
            dimension: config.dimension,
            metrics: config.metrics,
            sortBy: config.sortBy,
            limit: config.limit,
          }),
        );
      });

      const allMetricIds = getAllMetricIds(basicMetrics);
      dispatch(
        fetchDateYearMetrics({
          storeId: performanceStoreId,
          dateRange: currentPeriod,
          dimension: { id: dimensionId },
          metrics: allMetricIds,
          category: MetricCategory.CONVERSION,
        }),
      );
    }
  }, [
    dispatch,
    performanceStoreId,
    performanceDateRange,
    isReportAPIAvailable,
    isKpiConfigLoaded,
    conversionCategory,
    dateRangeDays,
    dimensionId,
    isAlreadyFetched,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetLocationTreeState());
    };
  }, [dispatch]);

  const handleSetGoal = useSetGoalHandler("conversion");
  const handleDeleteGoal = useDeleteGoalHandler("conversion");

  return (
    <>
      {performanceStoreId && performanceDateRange && (
        <PerformanceContainer>
          <MetricsGrid>
            {conversionMetrics.map((metric) => {
              const goalEnabled = isMetricGoalEnabled(
                conversionCategory,
                metric.id,
              );
              return (
                <MetricCard
                  key={metric.id}
                  data={metric}
                  lineChartData={metricLineCharts[metric.id]}
                  onSetGoal={goalEnabled ? handleSetGoal : undefined}
                  onDeleteGoal={goalEnabled ? handleDeleteGoal : undefined}
                  goal={
                    goalEnabled
                      ? getMetricGoal(conversionCategory, metric.id)
                      : undefined
                  }
                />
              );
            })}
          </MetricsGrid>
          <ChartsGrid>
            {conversionPieCharts.map((chart) => (
              <PieChart key={chart.title} data={chart} />
            ))}
            {treeChartConfig && (
              <LocationTreeView
                key={`${performanceStoreId}-${performanceDateRange}`}
                metricId={treeChartConfig.metricId}
                title={treeChartConfig.title}
                dimensionIds={treeChartConfig.dimensionIds}
                sortBy={treeChartConfig.sortBy}
                limit={treeChartConfig.limit}
              />
            )}
          </ChartsGrid>
        </PerformanceContainer>
      )}
    </>
  );
};

export default ConversionComponent;
